import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash/debounce';
import classNames from 'classnames';
import { GeoJsonObject, FeatureCollection, Point } from 'geojson';
import Input from '@lib/components/Input/Input';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import Modal from '@lib/components/Modal/Modal';
import Map from '@lib/components/Map/Map';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { NOMINATIM_BASE } from '@lib/components/Map/constants';
import { MapSize } from '@lib/components/Map/enums';
import { ModalMode } from '@lib/components/Modal/enums';
import styles from './LocationSelect.module.scss';

export interface LocationValue {
  name: string;
  lat: number;
  lon: number;
  geoJson?: GeoJsonObject;
}

export interface LocationSelectProps {
  value?: LocationValue | null;
  onChange?: (value: LocationValue | null) => void;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  usePortal?: boolean;
}

interface LocationSuggestion {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
}

function isPointFeatureCollection(
  geoJson: GeoJsonObject,
): geoJson is FeatureCollection<Point> {
  if (geoJson.type !== 'FeatureCollection') return false;
  const fc = geoJson as FeatureCollection;
  return (
    fc.features &&
    fc.features.length > 0 &&
    fc.features[0].geometry?.type === 'Point'
  );
}

function LocationSelect({
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  usePortal = false,
}: LocationSelectProps) {
  const { t, i18n } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempMapValue, setTempMapValue] = useState<GeoJsonObject | null>(null);
  const [tempLocationValue, setTempLocationValue] =
    useState<LocationValue | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const displayValue = value?.name || '';

  const searchPlace = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      try {
        const url = `${NOMINATIM_BASE}/search?format=json&limit=5&q=${encodeURIComponent(query)}&accept-language=${encodeURIComponent(i18n.language)}`;
        const response = await fetch(url, {
          headers: {
            Accept: 'application/json',
            'Accept-Language': i18n.language,
          },
        });
        const data = await response.json();
        // Validate API response is an array
        if (Array.isArray(data)) {
          setSuggestions(data);
          setHighlightedIndex(-1);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.error('Error searching places:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [i18n.language],
  );

  const debouncedSearchPlace = useMemo(
    () =>
      _debounce((query: string) => {
        searchPlace(query);
        if (query.trim()) {
          setShowSuggestions(true);
        }
      }, 500),
    [searchPlace],
  );

  // Cleanup debounced function on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedSearchPlace.cancel();
    };
  }, [debouncedSearchPlace]);

  // Handle click outside to close suggestions and reset editing state
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideInput = searchRef.current?.contains(target);
      const isInsideSuggestions = suggestionsRef.current?.contains(target);

      if (!isInsideInput && !isInsideSuggestions) {
        setShowSuggestions(false);
        // If user cleared the input while editing, clear the value
        if (isEditing && searchValue.trim() === '' && value) {
          onChange?.(null);
        }
        setIsEditing(false);
        setSearchValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, searchValue, value, onChange]);

  // Update dropdown position when using portal
  useEffect(() => {
    if (usePortal && showSuggestions && inputWrapperRef.current) {
      const updatePosition = () => {
        const rect = inputWrapperRef.current?.getBoundingClientRect();
        if (rect) {
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
    return undefined;
  }, [usePortal, showSuggestions]);

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value: inputValue } = e.target;
    setSearchValue(inputValue);
    if (inputValue.trim()) {
      setIsLoading(true);
    }
    debouncedSearchPlace(inputValue);
  };

  const handleSuggestionClick = (suggestion: LocationSuggestion) => {
    const newValue: LocationValue = {
      name: suggestion.display_name,
      lat: parseFloat(suggestion.lat),
      lon: parseFloat(suggestion.lon),
    };

    if (onChange) {
      onChange(newValue);
    }

    setSearchValue('');
    setShowSuggestions(false);
    setIsEditing(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1,
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleOpenModal = () => {
    if (disabled) return;

    // Initialize map and temp location with current value if exists
    if (value && value.lat && value.lon) {
      const geoJson: GeoJsonObject = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {
              address: value.name,
            },
            geometry: {
              type: 'Point',
              coordinates: [value.lon, value.lat],
            },
          },
        ],
      };
      setTempMapValue(geoJson);
      setTempLocationValue(value);
    } else {
      setTempMapValue({
        type: 'FeatureCollection',
        features: [],
      });
      setTempLocationValue(null);
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    // Abort any ongoing reverse geocode request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsModalOpen(false);
    setTempMapValue(null);
    setTempLocationValue(null);
  };

  const handleMapChange = useCallback(
    async (geoJson: GeoJsonObject) => {
      setTempMapValue(geoJson);

      // Type guard to ensure we have a valid Point FeatureCollection
      if (!isPointFeatureCollection(geoJson)) {
        setTempLocationValue(null);
        return;
      }

      const { features } = geoJson;
      if (features && features.length > 0) {
        const { coordinates } = features[0].geometry;
        const lat = coordinates[1];
        const lon = coordinates[0];

        // Cancel previous request if any
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        // Reverse geocode to get location name
        try {
          const url = `${NOMINATIM_BASE}/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=${encodeURIComponent(i18n.language)}`;
          const response = await fetch(url, {
            headers: {
              Accept: 'application/json',
              'Accept-Language': i18n.language,
            },
            signal: abortControllerRef.current.signal,
          });
          const data = await response.json();

          // Store in temp state - will be saved when user clicks Save
          setTempLocationValue({
            name: data.display_name || `${lat.toFixed(6)}, ${lon.toFixed(6)}`,
            lat,
            lon,
            geoJson,
          });
        } catch (error) {
          // Ignore abort errors, handle other errors
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }
          // Fallback to coordinates as name
          setTempLocationValue({
            name: `${lat.toFixed(6)}, ${lon.toFixed(6)}`,
            lat,
            lon,
            geoJson,
          });
        }
      } else {
        setTempLocationValue(null);
      }
    },
    [i18n.language],
  );

  const handleSaveLocation = () => {
    if (tempLocationValue && onChange) {
      onChange(tempLocationValue);
    }
    handleCloseModal();
  };

  const renderSuggestionsContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <CircleLoader size={20} padding={0} />
        </div>
      );
    }

    if (suggestions.length === 0) {
      return null;
    }

    return suggestions.map((suggestion, index) => (
      <div
        key={suggestion.place_id}
        className={classNames(styles.suggestion, {
          [styles.suggestionHighlighted]: index === highlightedIndex,
        })}
        onClick={() => handleSuggestionClick(suggestion)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSuggestionClick(suggestion);
          }
        }}
        role="option"
        aria-selected={index === highlightedIndex}
        tabIndex={-1}
      >
        {suggestion.display_name}
      </div>
    ));
  };

  const renderSuggestions = () => {
    const shouldShowDropdown =
      showSuggestions && (suggestions.length > 0 || isLoading);

    if (!shouldShowDropdown) {
      return null;
    }

    const suggestionsContent = (
      <div
        ref={suggestionsRef}
        className={styles.suggestions}
        role="listbox"
        style={
          usePortal && dropdownPosition
            ? {
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }
            : undefined
        }
      >
        {renderSuggestionsContent()}
      </div>
    );

    if (usePortal) {
      return createPortal(suggestionsContent, document.body);
    }

    return suggestionsContent;
  };

  return (
    <>
      <div className={styles.locationSelect}>
        <div
          ref={(node) => {
            searchRef.current = node;
            inputWrapperRef.current = node;
          }}
          className={styles.inputWrapper}
          data-has-value={!!displayValue}
        >
          <Input
            placeholder={placeholder || t('location')}
            value={isEditing ? searchValue : displayValue}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onClick={() => {
              if (!isEditing) {
                setSearchValue(displayValue);
                setIsEditing(true);
              }
            }}
            onClear={() => {
              setSearchValue('');
              setShowSuggestions(false);
              setIsEditing(false);
              onChange?.(null);
            }}
            leftIcon="search"
            hasError={!!hasError}
            disabled={!!disabled}
          />
          {renderSuggestions()}
        </div>
        <IconButton
          icon="map"
          type={IconButtonTypes.PrimaryOutlined}
          onClick={handleOpenModal}
          disabled={disabled}
          symbolsOutlined
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={ModalMode.FullScreen}
        modalContentClassName={styles.modalContentClassName}
        modalBodyClassName={styles.modalBodyClassName}
        fixedActions={
          <div className={styles.mapActions}>
            <ActionsRow
              actions={[
                <Button
                  key="cancel"
                  buttonText={t('cancel')}
                  buttonType={ButtonTypes.secondaryOutlined}
                  leftIcon="close"
                  onClick={handleCloseModal}
                  fullWidth
                />,
                <Button
                  key="save"
                  buttonText={t('save-location')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="check"
                  onClick={handleSaveLocation}
                  disabled={!tempLocationValue}
                  fullWidth
                />,
              ]}
            />
          </div>
        }
      >
        <div className={styles.mapContainer}>
          {tempMapValue && (
            <Map
              value={tempMapValue}
              onChange={handleMapChange}
              maxMarkers={1}
              size={MapSize.Full}
              bordered={false}
            />
          )}
        </div>
      </Modal>
    </>
  );
}

export default LocationSelect;
